import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { LegacyConfig, LegacyConfigService, StoreConfig, StoreConfigService } from '@scalefast/config-angular';
import { CartProduct, createCartProductFromProduct, Product } from '@scalefast/core';
import { AddToCartService } from '@scalefast/ecommerce-core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { switchMap, take } from 'rxjs/operators';
import { AppModalComponent } from 'src/app/shared/components/modal/modal.component';

import { NgClass } from '@angular/common';
import { ConfigCarousel } from '../../../core/interfaces/';
import { AppProductAddedToCartComponent } from '../../../pages/product/product-added-to-cart/product-added-to-cart.component';
import { AppProductCardComponent } from '../../../pages/product/product-card/product-card.component';
import { AppCrossSellProductsTemplateComponent } from '../cross-sell-products/cross-sell-products-template/cross-sell-products-template.component';
@Component({
  standalone: true,
  selector: 'app-product-carousel',
  styleUrls: ['./product-carousel.component.scss'],
  templateUrl: './product-carousel.component.html',
  imports: [
    AppProductCardComponent,
    NgClass,
    FormsModule,
    AppModalComponent,
    AppProductAddedToCartComponent,
    AppCrossSellProductsTemplateComponent,
  ],
})
export class AppProductCarouselComponent implements OnDestroy {
  @Input() productList: Array<Product> | null | undefined;
  @Input() title: string | undefined;
  @Input() public configCarousel: ConfigCarousel = {};

  @ViewChild('ModalComponent') modalComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponent') modalErrorComponent: AppModalComponent = new AppModalComponent();
  @ViewChild('ModalErrorComponentCartMaxValue') modalErrorComponentCartMaxValue: AppModalComponent = new AppModalComponent();

  localeId: string;
  currentSlide: number = 0;
  sliderKeen?: KeenSliderInstance;
  dotHelper: Array<Number> = [];

  showArrows: boolean = true;
  showDots: boolean = true;
  slidesPerView: number = 4;
  slidesNumber: number | undefined;
  activeDot: number = 1;
  visibleSlides: number[] = [];
  quantity = 1;
  product: Product | undefined;
  uniqueModalId: string = this.generateUniqueId();
  uniqueErrorModalId: string | undefined;
  addedToCartProduct: Product | undefined;
  addedToCartCartProduct: CartProduct | undefined;
  private storeConfig: StoreConfig;
  MAX_ITEM_QUANTITY: number;
  colorReferences: Record<string, string> | undefined = {};
  private legacyConfig: LegacyConfig;
  protected showQuantityErrorModal = false;
  productCard: AppProductCardComponent | undefined;
  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;
  showAddedToCartModal: boolean = false;
  modalTitle = $localize`:@@product.addedToCart:Successfully added to your cart!`;
  constructor(
    private cdRef: ChangeDetectorRef,
    private addToCartService: AddToCartService,
    private legacyConfigService: LegacyConfigService,
    private destroyRef: DestroyRef,
    private storeConfigService: StoreConfigService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.storeConfig = this.storeConfigService.get();
    this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;
    this.legacyConfig = this.legacyConfigService.get();
    this.uniqueModalId = this.generateUniqueId();
    this.localeId = this.locale;
    this.colorReferences = this.legacyConfig.metacolorReferences;
    afterNextRender(() => {
      this.createSlider();
    });
  }

  private createSlider() {
    if (!this.productList) {
      throw new Error('Product list is not defined');
    }
    this.slidesNumber = this.productList.length;
    this.sliderKeen = new KeenSlider(this.sliderRef.nativeElement, {
      initial: this.currentSlide,
      vertical: false,
      slides: {
        perView: () => {
          this.slidesPerView = 4;
          this.getShowArrowsDots();
          this.getDotsNumber();
          this.getActiveDot();
          this.updateVisibleSlides();
          return 4;
        },
        spacing: 20,
      },
      breakpoints: {
        '(max-width: 992px)': {
          slides: {
            perView: () => {
              this.slidesPerView = 2;
              this.getShowArrowsDots();
              this.getDotsNumber();
              this.getActiveDot();
              this.updateVisibleSlides();
              return 2;
            },
            spacing: 10,
          },
        },
        '(max-width: 768px)': {
          slides: {
            perView: () => {
              this.slidesPerView = 1;
              this.getShowArrowsDots();
              this.getDotsNumber();
              this.getActiveDot();
              this.updateVisibleSlides();
              return 1;
            },
          },
        },
      },
      slideChanged: (s) => {
        this.currentSlide = s.track.details.rel;
        this.getActiveDot();
        this.updateVisibleSlides();
      },
    });
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.sliderKeen?.destroy(); //Noop in server initialized in server
  }

  getShowArrowsDots() {
    if (this.slidesNumber! <= this.slidesPerView) {
      this.showArrows = false;
      this.showDots = false;
    } else {
      this.showArrows = true;
      this.showDots = true;
    }
  }

  getDotsNumber() {
    if (this.slidesNumber && this.showDots) {
      let dotsNumber = Math.ceil(this.slidesNumber / this.slidesPerView);
      this.dotHelper = [...Array(dotsNumber).keys()];
    }
  }

  goToSlide(dotActive: number) {
    if (!this.productList) {
      throw new Error('Product list is not defined');
    }
    let slideNumber = this.slidesPerView * dotActive;
    if (this.productList.length < slideNumber + this.slidesPerView - 1) {
      this.sliderKeen?.moveToIdx(this.productList.length - this.slidesPerView);
    } else {
      this.sliderKeen?.moveToIdx(slideNumber);
    }
  }

  // Use this function in prev button to navigate through slider using slidesperview steps
  prevStep() {
    let slideNumber = this.slidesPerView * (this.activeDot - 1);
    this.sliderKeen?.moveToIdx(slideNumber);
  }

  // Use this function in next button to navigate through slider using slidesperview steps
  nextStep() {
    if (!this.productList) {
      throw new Error('Product list is not defined');
    }
    let slideNumber = this.slidesPerView * (this.activeDot + 1);
    if (this.productList.length < slideNumber + this.slidesPerView - 1) {
      this.sliderKeen?.moveToIdx(this.productList.length - this.slidesPerView);
    } else {
      this.sliderKeen?.moveToIdx(slideNumber);
    }
  }

  getActiveDot() {
    this.dotHelper.map((dot, i) => {
      if (!this.productList) {
        throw new Error('Product list is not defined');
      }
      if (this.currentSlide === this.productList.length - this.slidesPerView) {
        this.activeDot = Number(this.dotHelper[this.dotHelper.length - 1]);
      } else if (this.currentSlide + 1 <= this.slidesPerView * (i + 1) && this.currentSlide + 1 > this.slidesPerView * i) {
        this.activeDot = i;
      }
    });
  }

  getVisibleSlides() {
    let visibleSlides: number[] = [];
    // Round slidesPerView to the closest number
    const slidesPerView = Math.round(this.slidesPerView);

    if (this.slidesNumber) {
      for (let i = 0; i < slidesPerView; i++) {
        let index = (this.currentSlide + i) % this.slidesNumber; // Infinite loop
        visibleSlides.push(index);
      }
    }
    return visibleSlides;
  }

  updateVisibleSlides() {
    this.visibleSlides = this.getVisibleSlides();
  }

  isVisibleSlide(index: number): boolean {
    return this.visibleSlides.includes(index);
  }

  onAddToCart(product: Product) {
    this.product = product;
    this.quantity = 1;
    if (!this.product) {
      return;
    }
    this.addToCartService
      .maxOrderValueReached(this.quantity)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((maxOrderValueReached) => {
        if (maxOrderValueReached) {
          this.showQuantityErrorModal = false;
          this.uniqueModalId = this.generateUniqueId();
          this.modalErrorComponentCartMaxValue.show();
          return;
        }
        let cartProduct = createCartProductFromProduct(product);
        if (cartProduct.quantity > this.MAX_ITEM_QUANTITY) {
          this.showQuantityErrorModal = true;
          this.modalErrorComponent.show();
          return;
        }

        this.addToCartService
          .getProductQuantity(product.id)
          .pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef),
            switchMap((productQuantity) => {
              //alert("Product quantity" + productQuantity);
              this.showQuantityErrorModal = false;
              this.uniqueModalId = this.generateUniqueId();
              this.addedToCartProduct = this.product;
              if (productQuantity + cartProduct.quantity > this.MAX_ITEM_QUANTITY) {
                this.showQuantityErrorModal = true;
                this.showAddedToCartModal = false;
                this.modalErrorComponent.show();
              } else {
                this.showAddedToCartModal = true;
                return this.addToCartService.onAddToCart(cartProduct);
              }
              return '';
            }),
          )
          .subscribe(() => {
            if (!this.showQuantityErrorModal) {
              this.showAddedToCartModal = false;
              this.uniqueModalId = this.generateUniqueId();
              this.modalComponent.show();
              return;
            }
          });
      });
  }

  generateUniqueId(): string {
    return Math.random().toString();
  }

  closeAddedToCartModal(): void {
    this.modalComponent.close();
  }

  closeQuantityErrorModalCarousel(): void {
    this.modalErrorComponent.close();
  }

  closeQuantityErrorCartMaxValueModal(): void {
    this.modalErrorComponentCartMaxValue.close();
  }
}
